import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Camera, FileText, Image as ImageIcon, Link2, Paperclip, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { captureScreenshot } from '@/lib/support/screenshot'
import { checkFiles, formatBytes, isImage } from '@/lib/support/supportFiles'
import { cn } from '@/lib/utils'

/**
 * Pending files for a report or a reply: picked, dropped, pasted or taken as
 * a screenshot (reviewed first). One hook owns the list and its refusals so
 * the dialog and the composer behave the same.
 */
export function usePendingFiles() {
  const [files, setFiles] = useState<File[]>([])
  const [refused, setRefused] = useState<string[]>([])
  const add = (incoming: File[]) => {
    const { accepted, rejected } = checkFiles(files, incoming)
    setFiles((cur) => [...cur, ...accepted])
    setRefused(rejected.map((r) => `${r.name}: ${r.reason}`))
  }
  return {
    files,
    refused,
    add,
    remove: (i: number) => setFiles((cur) => cur.filter((_, j) => j !== i)),
    clear: () => {
      setFiles([])
      setRefused([])
    },
  }
}
export type PendingFiles = ReturnType<typeof usePendingFiles>

/** Wraps an area so files can be dropped on it or pasted into it. */
export function FileDropZone({ pending, children, className }: { pending: PendingFiles; children: ReactNode; className?: string }) {
  const [over, setOver] = useState(false)
  return (
    <div
      className={cn('relative', className)}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (!e.dataTransfer.files.length) return
        e.preventDefault()
        setOver(false)
        pending.add([...e.dataTransfer.files])
      }}
      onPaste={(e) => {
        const pasted = [...e.clipboardData.files]
        if (pasted.length) {
          e.preventDefault()
          pending.add(pasted)
        }
      }}
    >
      {children}
      {over ? (
        <div className='pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/5 text-sm font-medium text-primary'>
          Drop files to attach
        </div>
      ) : null}
    </div>
  )
}

/** Attach · Screenshot · Link, as small ghost buttons. */
export function AttachmentTools({ pending, onLink, disabled }: { pending: PendingFiles; onLink?: (url: string) => void; disabled?: boolean }) {
  const input = useRef<HTMLInputElement>(null)
  const [shot, setShot] = useState<File | null>(null)
  const [shotError, setShotError] = useState<string | null>(null)
  const [linking, setLinking] = useState(false)
  return (
    <div className='flex flex-wrap items-center gap-1'>
      <input
        ref={input}
        type='file'
        multiple
        hidden
        onChange={(e) => {
          pending.add([...(e.target.files ?? [])])
          e.target.value = ''
        }}
      />
      <ToolButton icon={Paperclip} label='Attach files' disabled={disabled} onClick={() => input.current?.click()} />
      <ToolButton
        icon={Camera}
        label='Screenshot'
        disabled={disabled}
        onClick={() => {
          setShotError(null)
          captureScreenshot()
            .then(setShot)
            .catch((err: Error) => {
              if (err.name !== 'NotAllowedError') setShotError(err.message)
            })
        }}
      />
      {onLink ? <ToolButton icon={Link2} label='Add link' disabled={disabled} onClick={() => setLinking(true)} /> : null}
      {shotError ? <span className='text-xs text-destructive'>{shotError}</span> : null}
      {shot ? (
        <ScreenshotReview
          file={shot}
          onDiscard={() => setShot(null)}
          onAttach={() => {
            pending.add([shot])
            setShot(null)
          }}
        />
      ) : null}
      {linking && onLink ? (
        <LinkDialog
          onClose={() => setLinking(false)}
          onAdd={(url) => {
            onLink(url)
            setLinking(false)
          }}
        />
      ) : null}
    </div>
  )
}

function ToolButton({ icon: Icon, label, onClick, disabled }: { icon: typeof Paperclip; label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      className='inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50'
    >
      <Icon className='h-3.5 w-3.5' aria-hidden />
      {label}
    </button>
  )
}

/** The pending files as removable chips (images with a thumbnail). */
export function AttachmentTray({ pending }: { pending: PendingFiles }) {
  if (!pending.files.length && !pending.refused.length) return null
  return (
    <div className='flex flex-col gap-1.5'>
      {pending.files.length ? (
        <ul className='flex flex-wrap gap-2'>
          {pending.files.map((f, i) => (
            <li key={`${f.name}-${i}`} className='flex items-center gap-2 rounded-lg border border-border bg-background py-1 pl-1 pr-2 text-xs'>
              <FileThumb file={f} />
              <span className='max-w-[180px] truncate font-medium text-foreground' title={f.name}>
                {f.name}
              </span>
              <span className='text-muted-foreground'>{formatBytes(f.size)}</span>
              <button type='button' aria-label={`Remove ${f.name}`} className='rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground' onClick={() => pending.remove(i)}>
                <X className='h-3 w-3' />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {pending.refused.map((r) => (
        <p key={r} className='text-xs text-destructive'>
          {r}
        </p>
      ))}
    </div>
  )
}

function FileThumb({ file }: { file: File }) {
  const url = useMemo(() => (isImage(file.type) ? URL.createObjectURL(file) : null), [file])
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url])
  if (url) return <img src={url} alt='' className='h-7 w-7 rounded object-cover' />
  return (
    <span className='flex h-7 w-7 items-center justify-center rounded bg-muted text-muted-foreground'>
      <FileText className='h-3.5 w-3.5' aria-hidden />
    </span>
  )
}

/** A screenshot is shown before it is attached: the person checks it shows no patient identifiers. */
function ScreenshotReview({ file, onAttach, onDiscard }: { file: File; onAttach: () => void; onDiscard: () => void }) {
  const url = useMemo(() => URL.createObjectURL(file), [file])
  useEffect(() => () => URL.revokeObjectURL(url), [url])
  const [checked, setChecked] = useState(false)
  return (
    <Dialog open onOpenChange={(o) => !o && onDiscard()}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <ImageIcon className='h-4 w-4' aria-hidden /> Check the screenshot
          </DialogTitle>
          <DialogDescription>It goes to Title21 Support. Make sure it shows no patient names, MRNs or DINs.</DialogDescription>
        </DialogHeader>
        <img src={url} alt='Screenshot to attach' className='max-h-[55vh] w-full rounded-md border border-border object-contain' />
        <label className='flex items-center gap-2 text-sm text-foreground'>
          <input type='checkbox' checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          It shows no patient identifiers
        </label>
        <DialogFooter>
          <Button variant='outline' onClick={onDiscard}>
            Discard
          </Button>
          <Button disabled={!checked} onClick={onAttach}>
            Attach screenshot
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Paste a link (a CGT page, a document) to add to the message. */
function LinkDialog({ onAdd, onClose }: { onAdd: (url: string) => void; onClose: () => void }) {
  const [url, setUrl] = useState(typeof window === 'undefined' ? '' : window.location.href)
  const valid = /^https?:\/\/\S+$/.test(url.trim())
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>Add a link</DialogTitle>
          <DialogDescription>A CGT page, a document, anything Title21 should open.</DialogDescription>
        </DialogHeader>
        <Input autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder='https://…' />
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid} onClick={() => onAdd(url.trim())}>
            Add link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
